import {
  Anchor,
  Badge,
  Divider,
  Group,
  Paper,
  Radio,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconCheck, IconShieldCheck, IconStar } from "@tabler/icons-react";
import classes from "./SingleIssuanceWizard.module.css";

const formatCoverage = (currency, amount) => {
  if (amount == null || amount === "") return "—";
  const num = Number(amount);
  if (Number.isNaN(num)) return `${currency} ${amount}`.trim();
  return `${currency} ${num.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`.trim();
};

const BenefitRow = ({ benefit, optionType, index }) => {
  const cov = (benefit.coverages || []).find((c) => c.type === optionType);
  const hasDeductible =
    benefit.deductible_amount != null && benefit.deductible_amount !== "";

  return (
    <div
      className={`${classes.benefitGridRow} ${
        index % 2 === 1 ? classes.benefitGridRowAlt : ""
      }`}
    >
      <Group gap={8} wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
        <IconCheck size={14} color="#003594" stroke={2.5} style={{ flexShrink: 0, marginTop: 2 }} />
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Text size="sm" lh={1.35}>
            {benefit.benefit_name}
          </Text>
          {hasDeductible ? (
            <Text size="xs" c="dimmed">
              Deductible {benefit.deductible_amount}
            </Text>
          ) : null}
        </Stack>
      </Group>
      <Text size="sm" fw={600} c="#003594" ta="right" className={classes.benefitCoverage}>
        {formatCoverage(benefit.currency, cov?.amount)}
      </Text>
    </div>
  );
};

const BenefitsTable = ({ plan, optionType }) => {
  const benefits = plan.benefits || [];
  if (!benefits.length) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="md">
        No benefits listed for this plan.
      </Text>
    );
  }

  const splitAt = Math.ceil(benefits.length / 2);
  const columns = [benefits.slice(0, splitAt), benefits.slice(splitAt)].filter(
    (col) => col.length > 0
  );
  const useCompactScroll = benefits.length > 12;

  const grid = (
    <div className={classes.benefitsTable}>
      <div className={classes.benefitsTableHead}>
        <Text span className={classes.benefitsTableHeadCell}>
          Benefit
        </Text>
        <Text span className={classes.benefitsTableHeadCell} ta="right">
          Sum insured
        </Text>
      </div>
      <SimpleGrid
        cols={{ base: 1, md: columns.length > 1 ? 2 : 1 }}
        spacing={0}
      >
        {columns.map((column, colIndex) => (
          <div
            key={colIndex}
            className={`${classes.benefitsColumn} ${
              colIndex === 1 ? classes.benefitsColumnSplit : ""
            }`}
          >
            {column.map((benefit, rowIndex) => (
              <BenefitRow
                key={benefit.benefit_name}
                benefit={benefit}
                optionType={optionType}
                index={rowIndex + colIndex * splitAt}
              />
            ))}
          </div>
        ))}
      </SimpleGrid>
    </div>
  );

  if (!useCompactScroll) return grid;

  return (
    <ScrollArea.Autosize mah={300} type="auto" offsetScrollbars>
      {grid}
    </ScrollArea.Autosize>
  );
};

const CoverPlanOptions = ({ plan, coverOption, onCoverChange }) => {
  const selectedOpt = (plan.options || []).find((o) => o.option_type === coverOption);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
        <div>
          <Text fw={600}>{plan.title}</Text>
          <Text size="xs" c="dimmed">
            {plan.duration_days} day(s) · {plan.persons} pax
          </Text>
        </div>
        {plan.brochure ? (
          <Anchor href={plan.brochure} target="_blank" size="sm">
            Brochure
          </Anchor>
        ) : null}
      </Group>

      <Radio.Group
        value={coverOption}
        onChange={onCoverChange}
        label="Select your cover"
        withAsterisk
        color="brand"
      >
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md" mt="sm">
          {(plan.options || []).map((opt, idx) => {
            const selected = coverOption === opt.option_type;
            return (
              <Paper
                key={opt.option_type}
                withBorder
                p="md"
                radius="md"
                className={`${classes.optionCard} ${selected ? classes.optionCardSelected : ""}`}
                onClick={() => onCoverChange(opt.option_type)}
                style={{ cursor: "pointer" }}
              >
                <Radio
                  value={opt.option_type}
                  color="brand"
                  onChange={() => onCoverChange(opt.option_type)}
                  label={
                    <Stack gap={2}>
                      <Text size="xs" fw={700} c="#003594" tt="uppercase">
                        {opt.option_type}
                      </Text>
                      <Text fw={700} c="#003594">
                        {opt.currency_symbol}
                        {Number(opt.total_premium).toLocaleString("en-IN")}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {opt.currency_symbol}
                        {Number(opt.per_person_avg).toLocaleString("en-IN")} Per person
                      </Text>
                    </Stack>
                  }
                  styles={{ body: { alignItems: "flex-start" } }}
                />
                {(opt.age_pricing || []).map((ap) => (
                  <Text key={`${ap.age}-${ap.pricing_id}`} size="xs" c="dimmed" mt={4} ml={28}>
                    Age {ap.age}: {opt.currency_symbol}
                    {Number(ap.price).toLocaleString("en-IN")} ({ap.duration}d)
                  </Text>
                ))}
                {idx === 0 && selected ? (
                  <Badge
                    color="brand"
                    variant="light"
                    size="sm"
                    mt="sm"
                    leftSection={<IconStar size={12} />}
                  >
                    Most Selected
                  </Badge>
                ) : null}
              </Paper>
            );
          })}
        </SimpleGrid>
      </Radio.Group>

      {selectedOpt ? (
        <Paper radius="md" mt="md" className={classes.benefitsCard}>
          <div className={classes.benefitsHeader}>
            <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon
                  size={40}
                  radius="md"
                  variant="gradient"
                  gradient={{ from: "#003594", to: "#0053A8", deg: 135 }}
                >
                  <IconShieldCheck size={22} stroke={1.75} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text fw={700} c="#003594">
                    Plan Benefits
                  </Text>
                  <Text size="xs" c="dimmed">
                    {(plan.benefits || []).length} coverage
                    {(plan.benefits || []).length === 1 ? "" : "s"} ·{" "}
                    {plan.duration_days} day plan
                  </Text>
                </Stack>
              </Group>
              <Badge size="lg" radius="sm" color="brand" variant="filled" tt="uppercase">
                {selectedOpt.option_type}
              </Badge>
            </Group>
          </div>
          <Divider mx="md" color="rgba(0, 53, 148, 0.12)" />
          <div className={classes.benefitsBody}>
            <BenefitsTable plan={plan} optionType={selectedOpt.option_type} />
          </div>
        </Paper>
      ) : (
        <Text size="xs" c="dimmed" ta="center">
          Select an option to view benefit details
        </Text>
      )}
    </Stack>
  );
};

export default CoverPlanOptions;
